import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  generalConstants,
  operationMessages,
  roleContextMapping,
} from '@config';
import { environment } from '@environment';
import {
  AuthSession,
  Email,
  Favorite,
  Profile,
  UserContext,
  Role,
  Permission,
  MutationResponse,
} from '@models';
import * as jwtDecode from 'jwt-decode';
import { camelCase, mapKeys, upperFirst, isEmpty } from 'lodash';
import { NgxPermissionsObject } from 'ngx-permissions';
import {
  BehaviorSubject,
  Observable,
  of,
  throwError,
  forkJoin,
  combineLatest,
} from 'rxjs';
import { map, tap, take } from 'rxjs/operators';

import { UserServiceGql, UpdateUserInput, UpdateUserPayload } from '../gql';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';
import { StorageService } from './storage.service';
import { CompanyService } from './company.service';
export const userStorageKey = 'userSession';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public favorites$: BehaviorSubject<Partial<Favorite[]>>;
  permissions: NgxPermissionsObject;
  roles: any;

  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
    private storageService: StorageService,
    private companyService: CompanyService,
    private userServiceGql: UserServiceGql,
    private permissionService: PermissionService
  ) {
    this.permissionService.getPermissions().subscribe(perm => {
      this.permissions = perm;
    });
  }

  public updateAvatar(file: File): Observable<{ [Key: string]: string }> {
    return this.userServiceGql.updateUserAvatar(file).pipe(
      map(data => {
        this.getCurrentUserInformation().subscribe(res => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { avatarUrl, username, ...rest } = res;

          return this.authService.updateProfile({
            avatarUrl: data.fileUrl,
            ...rest,
          });
        });

        return { imageUrl: data.fileUrl };
      })
    );
  }

  public updateFavorite(favorite: Favorite[]): Observable<any> {
    // The real implementation will consume an endpoint.
    let allValues;

    this.storageService
      .getItem<Partial<AuthSession>>(userStorageKey)
      .subscribe(session => (allValues = session));
    let favorites = allValues.favorites;

    if (favorites) {
      if (favorites.find(v => v.id === favorite[0].id)) {
        favorites = favorites.filter(v => v.id !== favorite[0].id);
      } else {
        favorites = [...favorites, favorite[0]];
      }
    }

    this.storageService.setItem(userStorageKey, { ...allValues, favorites });

    if (this.favorites$) {
      this.favorites$.next(favorites);
    }

    return of(favorites);
  }

  public resetPassword(userData: any) {
    const route = `${environment.apiUrl}/user/lfx-reset-password`;

    return this.httpClient.post<boolean>(route, userData);
  }

  public checkUserPermissions(
    permissionRequest: string[]
  ): Observable<Permission[]> {
    return this.userServiceGql.checkUserPermissions(permissionRequest);
  }

  public getCurrentUserInformation(): Observable<Profile> {
    return forkJoin(
      this.userServiceGql.getCurrentUser(false),
      this.authService.userProfile$.asObservable().pipe(take(1))
    ).pipe(
      map(([user, profile]) =>
        user
          ? {
              salesforceId: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              primaryEmail:
                user.emails &&
                user.emails.filter(email => email.isPrimary)[0].emailAddress,
              emails:
                user.emails
                  .slice()
                  .sort(
                    (first, second) =>
                      Number(second.isPrimary) - Number(first.isPrimary)
                  ) || [],
              timezone: user.timezone,
              avatarUrl:
                user.avatarUrl ||
                profile.avatarUrl ||
                generalConstants.defaultAvatar,
              username: user.username,
              socialAccounts: user.socialAccounts,
            }
          : null
      )
    );
  }

  public initializeFavourite() {
    return this.storageService
      .getItem<Partial<Favorite>>(userStorageKey)
      .subscribe((res: any) => {
        if (res && res.favorites) {
          const { favorites } = res;

          this.favorites$ = new BehaviorSubject(
            favorites as Partial<Favorite[]>
          );
        } else {
          const favorites = [];

          this.storageService.setItem(userStorageKey, { favorites });
          this.favorites$ = new BehaviorSubject(
            favorites as Partial<Favorite[]>
          );
        }
      });
  }

  public getUserContexts(): Observable<UserContext[]> {
    return combineLatest(
      this.companyService.getMyCompany(),
      this.getUserRoles()
    ).pipe(
      map(([company, userRoles]: any[]) => {
        if (company && company.name === generalConstants.individualNoAccount) {
          return [UserContext.Individual];
        } else {
          if (!userRoles) {
            return [];
          }

          return [...new Set(this.mapRolesToContexts(userRoles))];
        }
      })
    );
  }

  public updateUser(userInfo: UpdateUserInput): Observable<UpdateUserPayload>;
  public updateUser(
    id: string,
    profile: Partial<Profile>
  ): Observable<UpdateUserPayload>;

  public updateUser(
    input: string | UpdateUserInput,
    profile?: Partial<Profile>
  ): Observable<UpdateUserPayload> {
    if (profile) {
      const { firstName, lastName, emails } = profile;
      const id = input as string;
      const dataToBeUpdated = { id, firstName, lastName, emails };

      return this.userServiceGql.updateUser(dataToBeUpdated).pipe(
        map((response: any) => {
          if (!response.user) {
            throw new Error(response.message);
          }

          return response;
        })
      );
    }

    return this.userServiceGql.updateUser(input as UpdateUserInput);
  }

  public updateProfile(profile: Partial<Profile>): Observable<Profile> {
    const {
      firstName,
      lastName,
      timezone,
      emails,
      username,
      avatarUrl,
    } = profile;

    const profileToBeUpdated = {
      firstName,
      lastName,
      timezone,
      emails,
      username,
      avatarUrl,
    };

    if (profileToBeUpdated.emails) {
      profileToBeUpdated.emails = this.parseEmails(
        profileToBeUpdated.emails,
        true
      );
    }

    return this.userServiceGql.updateCurrentUser(profileToBeUpdated).pipe(
      map((response: any) => {
        if (response.currentUser) {
          return {
            salesforceId: response.currentUser.id,
            firstName: response.currentUser.firstName,
            lastName: response.currentUser.lastName,
            primaryEmail: response.currentUser.primaryEmail,
            emails:
              (response.currentUser.emails &&
                this.parseEmails(response.currentUser.emails, true)
                  .slice()
                  .sort(
                    (first, second) =>
                      Number(second.isPrimary) - Number(first.isPrimary)
                  )) ||
              [],
            timezone: response.currentUser.timezone,
            avatarUrl:
              response.currentUser.avatarUrl || generalConstants.defaultAvatar,
            username: response.currentUser.username,
          } as Profile;
        }

        response.message =
          response.message === '409: Conflict'
            ? 'This email belongs to another LF user'
            : response.message;

        throw new Error(response.message);
      }),
      tap(updatedProfile => {
        this.authService.updateProfile(updatedProfile as Partial<Profile>);
      })
    );
  }

  public resendVerficationEmail(email: string, state: string): Observable<any> {
    const route = `${environment.apiUrl}/user/resend-verification-email`;

    const params: any = {
      email: encodeURIComponent(email),
    };

    if (state) {
      params.state = state;
    }

    return this.httpClient.post(route, null, { params });
  }

  public getUserServiceRoles(input: string): Observable<Role[]> {
    return this.userServiceGql
      .getUserRoles({ search: input })
      .pipe(map(roles => roles));
  }

  public listUsers() {
    return this.userServiceGql.listUsers();
  }

  public getInvite(inviteId: string): Observable<any> {
    return this.userServiceGql.getInvitation(inviteId);
  }

  updateInvite(inviteId: string, status: string): Observable<any> {
    return this.userServiceGql.updateInvitation(inviteId, status);
  }

  deleteInvite(inviteId: string): Observable<MutationResponse<null>> {
    return this.userServiceGql.deleteInvitation(inviteId);
  }

  isUserAdmin(companyId) {
    const orgDetailPatialUpdate = `org_detail:partial_update:organization:${companyId}`;

    return this.checkUserPermissions([orgDetailPatialUpdate]).pipe(
      map(data => data[0].status)
    );
  }

  hasAllPermission(permissions: string[]): Observable<boolean> {
    return this.checkUserPermissions(permissions).pipe(
      map(permissions =>
        permissions.reduce(
          (previous, current) => previous && current.status,
          true
        )
      )
    );
  }

  private mapRolesToContexts(roles: string[]): UserContext[] {
    return roles.map(r => roleContextMapping[r]);
  }

  private getUserRoles(): Observable<string[]> {
    if (this.roles) {
      return of(this.roles);
    }

    return this.userServiceGql
      .getCurrentUser()
      .pipe(
        tap(response => {
          let filtered = [];

          if (response && response.permissions) {
            filtered = response.permissions.filter(perm => {
              if (perm.scopes) {
                return (
                  perm.scopes.filter(
                    scope => scope.id.filter(id => id !== '*').length > 0
                  ).length > 0
                );
              }
            });
          }

          if (filtered.length > 0) {
            this.roles = ['contributor'];
          }
        })
      )
      .pipe(map((response: any) => (response ? response.roles : [])))
      .pipe(
        tap(value => {
          if (value && !isEmpty(value)) {
            if (this.roles) {
              this.roles.push(...value);
            } else {
              this.roles = value;
            }
          }
        })
      );
  }

  private handleProfileUpdateError(error, profile: Partial<Profile>) {
    let errorMessage = '';

    switch (error.status) {
      case 409:
        errorMessage = this.handleConflictError(error, profile);
        break;
      case 400:
        errorMessage = this.handleValidationError(error, profile);
        break;
      default:
        errorMessage = operationMessages.profile.updateError;
    }

    error.message = errorMessage || error.message;

    return throwError(error);
  }

  private handleConflictError(conflictError, profile: Partial<Profile>) {
    if (conflictError.message.includes('email')) {
      return this.getEmailConflictErrorMessage(conflictError, profile);
    }
  }

  private handleValidationError(validationError, profile: Partial<Profile>) {
    if (validationError.message.includes('email')) {
      return this.getEmailValidationErrorMessage(validationError, profile);
    }
  }

  private getEmailConflictErrorMessage(
    conflictError,
    profile: Partial<Profile>
  ) {
    const conflictEmail = this.getErrEmail(
      profile.emails,
      conflictError.message
    );

    return `${conflictEmail} ${operationMessages.profile.emailConflictError}`;
  }

  private getEmailValidationErrorMessage(
    validationError,
    profile: Partial<Profile>
  ) {
    const invalidEmail = this.getErrEmail(
      profile.emails,
      validationError.message
    );

    return `Email '${invalidEmail}' ${operationMessages.profile.partialUpdateEmailValidationError}`;
  }

  private getErrEmail(emails: Email[], conflictError: string): string {
    const emls = this.parseEmails(emails, true);

    const errEmail = emls.find(nextEmail =>
      conflictError.includes(nextEmail.emailAddress)
    );

    return (errEmail || ({} as Email)).emailAddress || '';
  }

  private parseEmails(emails: any[], toCamelCase: boolean) {
    return emails.map((email: any) =>
      mapKeys(email, (v, k) => (toCamelCase ? camelCase(k) : upperFirst(k)))
    ) as Email[];
  }

  private getUserIdFromIdToken(idToken: string): string {
    const userInfo = jwtDecode(idToken);

    return userInfo.sub;
  }
}
